import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import {
  Box, Button, Card, CardContent, Typography,
  Chip, Stack, IconButton, Tooltip, CircularProgress,
  useMediaQuery, useTheme,
} from '@mui/material';
import {
  PersonSearch, PersonAdd, Edit, DeleteOutline, CreditCard, LocalOffer, Replay, TravelExplore,
  ReceiptLong,
} from '@mui/icons-material';
import ClienteSearch from '../../../components/Tesoreria/ClienteSearch';
import FacturaPOSClienteSearchBar from './FacturaPOSClienteSearchBar';
import FacturaPOSNuevoClienteDialog from './FacturaPOSNuevoClienteDialog';
import clienteService from '../../../services/clienteService';
import type { Info360Severidad } from '../../clientes/info360/types';
import { POSCliente, POSLinea } from './types';
import { lineasBloqueanEdicionCliente, MSG_CLIENTE_BLOQUEADO_POR_MASTER } from './lineaMasterCliente';
import { formatCurrency } from '../../../utils/formatters';
import { resolverNombreCliente, labelTipoIvaCliente } from './clienteNombre';
import ClienteFacturacionAvisos from './ClienteFacturacionAvisos';
import { esClienteSuspendido } from '../../../utils/clienteEstadoFacturacion';
import type { BloqueoClienteSuspendidoModo } from '../../../utils/clienteEstadoFacturacion';
import { labelTipoCliente } from '../../../config/tipoClienteOptions';

interface Props {
  cliente: POSCliente | null;
  lineas?: POSLinea[];
  onClienteSelect: (cliente: POSCliente) => void;
  onClienteClear?: () => void;
  readOnly?: boolean;
  /** Condición de venta del documento; si está informada, el chip refleja este valor (p. ej. al editar en el panel lateral). */
  formaDePago?: string;
  /** Orden de compra del documento; en la tarjeta siempre se muestra la línea (vacío → «Sin dato»). */
  ordenCompra?: string;
  /** Si se informa, muestra el botón «Último pedido» cuando hay cliente. */
  onRepetirUltimoPedido?: () => void | Promise<void>;
  loadingRepetirUltimoPedido?: boolean;
  /** Si se informa, muestra el botón «Últimas facturas» cuando hay cliente. */
  onUltimasFacturas?: () => void;
  /** Abre la ficha Info 360 del cliente seleccionado. */
  onVerInfo360?: () => void;
  /** Modo configurado para clientes suspendidos en facturación. */
  bloqueoClienteSuspendido?: BloqueoClienteSuspendidoModo;
}

/** Color del ícono Info 360 según la recomendación comercial/crédito. */
function colorSemaforoInfo360(severidad: Info360Severidad | null): string {
  switch (severidad) {
    case 'danger':
      return '#d32f2f';
    case 'warning':
      return '#ed6c02';
    case 'ok':
      return '#2e7d32';
    case 'info':
      return '#0288d1';
    default:
      return '#757575';
  }
}

const FacturaPOSClientePanel: React.FC<Props> = ({
  cliente,
  lineas = [],
  onClienteSelect,
  onClienteClear,
  readOnly = false,
  formaDePago,
  ordenCompra,
  onRepetirUltimoPedido,
  loadingRepetirUltimoPedido = false,
  onUltimasFacturas,
  onVerInfo360,
  bloqueoClienteSuspendido = 'alert',
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchOpen, setSearchOpen] = useState(false);
  const [nuevoClienteOpen, setNuevoClienteOpen] = useState(false);
  const [info360Severidad, setInfo360Severidad] = useState<Info360Severidad | null>(null);
  const [info360Titulo, setInfo360Titulo] = useState<string | null>(null);
  const [loadingInfo360, setLoadingInfo360] = useState(false);

  useEffect(() => {
    const nroCliente = cliente?.nroCliente;
    if (!onVerInfo360 || !nroCliente || nroCliente <= 0) {
      setInfo360Severidad(null);
      setInfo360Titulo(null);
      setLoadingInfo360(false);
      return;
    }

    let cancelled = false;
    setLoadingInfo360(true);
    setInfo360Severidad(null);
    setInfo360Titulo(null);

    void clienteService.getClienteInfo360(nroCliente)
      .then((data) => {
        if (cancelled) return;
        setInfo360Severidad(data.recomendacion?.severidad ?? null);
        setInfo360Titulo(data.recomendacion?.titulo?.trim() || null);
      })
      .catch(() => {
        if (cancelled) return;
        setInfo360Severidad(null);
        setInfo360Titulo(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingInfo360(false);
      });

    return () => {
      cancelled = true;
    };
  }, [cliente?.nroCliente, onVerInfo360]);

  const handleSelect = (c: any) => {
    onClienteSelect(c as POSCliente);
    setSearchOpen(false);
  };

  const nroListaLabel =
    cliente?.listaPrecio?.descripcion
    ?? cliente?.listaPrecio?.codigoLista
    ?? cliente?.nroLista;

  /** Nombre mostrado en cabecera: el `nombre` del documento (editable en emisión) tiene prioridad sobre razón social del maestro. */
  const nombreClienteRes = cliente
    ? (String(cliente.nombre ?? '').trim() || resolverNombreCliente(cliente))
    : '';
  const tituloClienteCard = cliente
    ? (nombreClienteRes
      ? `${cliente.nroCliente} - ${nombreClienteRes}`
      : `Cliente #${cliente.nroCliente}`)
    : '';

  const condicionVentaChip =
    (formaDePago != null && String(formaDePago).trim() !== '')
      ? String(formaDePago).trim()
      : (cliente?.condVta ?? '').trim();

  const ordenCompraTrim = (ordenCompra ?? '').trim();

  const clienteEdicionBloqueada =
    !readOnly && lineasBloqueanEdicionCliente(lineas, cliente?.nroCliente ?? 0);

  const hayLineasConArticulo = lineas.some((l) => String(l.codigoArticulo ?? '').trim() !== '');

  const handleRepetirUltimoPedido = () => {
    if (!onRepetirUltimoPedido || loadingRepetirUltimoPedido) return;
    if (hayLineasConArticulo) {
      const ok = window.confirm(
        'Ya hay artículos en el documento. ¿Reemplazarlos con el último pedido del cliente?',
      );
      if (!ok) return;
    }
    void onRepetirUltimoPedido();
  };

  return (
    <Box>
      <ClienteSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSelect}
      />

      <FacturaPOSNuevoClienteDialog
        open={nuevoClienteOpen}
        onClose={() => setNuevoClienteOpen(false)}
        onCreated={handleSelect}
      />

      {clienteEdicionBloqueada ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mb: 1 }}>
          {MSG_CLIENTE_BLOQUEADO_POR_MASTER}
        </Typography>
      ) : null}

      {!readOnly && !cliente && !clienteEdicionBloqueada && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          flexWrap="wrap"
          useFlexGap
        >
          <Box sx={{ flex: 1, minWidth: { xs: 0, sm: 200 }, width: { xs: '100%', sm: 'auto' } }}>
            <FacturaPOSClienteSearchBar
              onClienteSelect={handleSelect}
              disabled={readOnly}
              onClienteInactivo={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
            />
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAdd />}
              onClick={() => setNuevoClienteOpen(true)}
              fullWidth={isMobile}
              sx={{ py: 1, px: 1.5, borderRadius: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Nuevo cliente
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonSearch color="primary" />}
              onClick={() => setSearchOpen(true)}
              fullWidth={isMobile}
              sx={{
                py: 1,
                px: 2,
                borderRadius: 2,
                textTransform: 'none',
                whiteSpace: 'nowrap',
                bgcolor: 'background.paper',
                borderColor: 'divider',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.main',
                },
              }}
            >
              Seleccionar cliente
            </Button>
          </Stack>
        </Stack>
      )}

      {cliente ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            ...(esClienteSuspendido(cliente.tipoCliente) && bloqueoClienteSuspendido === 'hard'
              ? { borderColor: 'error.main', borderWidth: 2 }
              : esClienteSuspendido(cliente.tipoCliente)
                ? { borderColor: 'warning.main', borderWidth: 2 }
                : {}),
          }}
        >
          <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1.5 } }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box flex={1} minWidth={0}>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ wordBreak: 'break-word', lineHeight: 1.25, mb: 0.5 }}
                >
                  {tituloClienteCard}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {cliente.cuit && (
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                      CUIT: {cliente.cuit}
                    </Typography>
                  )}
                  {cliente.tipoIva && (
                    <Chip
                      label={labelTipoIvaCliente(cliente.tipoIva)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {esClienteSuspendido(cliente.tipoCliente) ? (
                    <Chip
                      label={labelTipoCliente(cliente.tipoCliente)}
                      size="small"
                      color={bloqueoClienteSuspendido === 'hard' ? 'error' : 'warning'}
                      sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
                    />
                  ) : null}
                  {nroListaLabel && (
                    <Chip
                      icon={<LocalOffer sx={{ fontSize: '12px !important' }} />}
                      label={`Lista: ${nroListaLabel}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem', maxWidth: '100%' }}
                    />
                  )}
                  {condicionVentaChip ? (
                    <Chip
                      label={condicionVentaChip}
                      size="small"
                      sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'action.hover', maxWidth: '100%' }}
                    />
                  ) : null}
                </Stack>

                {cliente.credito != null && cliente.credito > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} mt={0.75}>
                    <CreditCard sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">Crédito:</Typography>
                    <Typography variant="caption" fontWeight={600} color="success.main">
                      {formatCurrency(cliente.credito)}
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" alignItems="baseline" spacing={2} flexWrap="wrap" useFlexGap mt={0.5}>
                  {cliente.vendedorInfo?.nombreVendedor ? (
                    <Typography variant="caption" color="text.secondary" component="span">
                      Vendedor: {cliente.vendedorInfo.nombreVendedor}
                    </Typography>
                  ) : null}
                  <Typography
                    variant="caption"
                    color={ordenCompraTrim ? 'text.secondary' : 'text.disabled'}
                    component="span"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    Orden de compra: {ordenCompraTrim || 'Sin dato'}
                  </Typography>
                </Stack>
              </Box>

              <Stack
                direction="row"
                spacing={0.5}
                flexShrink={0}
                alignItems="center"
                justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}
                flexWrap="wrap"
                useFlexGap
                sx={{ width: { xs: '100%', sm: 'auto' }, mt: { xs: 0.5, sm: 0 } }}
              >
                {onVerInfo360 ? (
                  <Tooltip
                    title={
                      info360Titulo
                        ? `Info 360 · ${info360Titulo}`
                        : loadingInfo360
                          ? 'Info 360 · evaluando indicadores…'
                          : 'Info 360 del cliente'
                    }
                  >
                    <IconButton
                      size="small"
                      onClick={onVerInfo360}
                      aria-label={
                        info360Titulo
                          ? `Info 360 del cliente: ${info360Titulo}`
                          : 'Info 360 del cliente'
                      }
                      sx={{
                        color: colorSemaforoInfo360(info360Severidad),
                        opacity: loadingInfo360 ? 0.55 : 1,
                        ...(info360Severidad === 'danger' ? {
                          animation: 'info360Riesgo 1.4s ease-in-out infinite',
                          '@keyframes info360Riesgo': {
                            '0%, 100%': {
                              transform: 'scale(1)',
                              filter: 'drop-shadow(0 0 0 rgba(211,47,47,0))',
                            },
                            '50%': {
                              transform: 'scale(1.18)',
                              filter: 'drop-shadow(0 0 5px rgba(211,47,47,0.75))',
                            },
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            animation: 'none',
                          },
                        } : {}),
                      }}
                    >
                      <TravelExplore fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null}
                {!readOnly && !clienteEdicionBloqueada ? (
                  <>
                    {onUltimasFacturas ? (
                      <Tooltip title="Copiar desde las últimas facturas del cliente">
                        <span>
                          {isMobile ? (
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={onUltimasFacturas}
                              aria-label="Últimas facturas"
                            >
                              <ReceiptLong fontSize="small" />
                            </IconButton>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<ReceiptLong fontSize="small" />}
                              onClick={onUltimasFacturas}
                              aria-label="Últimas facturas"
                              sx={{ textTransform: 'none', mr: 0.5, whiteSpace: 'nowrap' }}
                            >
                              Últimas facturas
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                    ) : null}
                    {onRepetirUltimoPedido ? (
                      <Tooltip title="Repetir último pedido del cliente">
                        <span>
                          {isMobile ? (
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={loadingRepetirUltimoPedido}
                              onClick={handleRepetirUltimoPedido}
                              aria-label="Último pedido"
                            >
                              {loadingRepetirUltimoPedido
                                ? <CircularProgress size={16} color="inherit" />
                                : <Replay fontSize="small" />}
                            </IconButton>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={
                                loadingRepetirUltimoPedido
                                  ? <CircularProgress size={14} color="inherit" />
                                  : <Replay fontSize="small" />
                              }
                              disabled={loadingRepetirUltimoPedido}
                              onClick={handleRepetirUltimoPedido}
                              aria-label="Último pedido"
                              sx={{ textTransform: 'none', mr: 0.5, whiteSpace: 'nowrap' }}
                            >
                              Último pedido
                            </Button>
                          )}
                        </span>
                      </Tooltip>
                    ) : null}
                    <Tooltip title="Cambiar cliente">
                      <IconButton size="small" onClick={() => setSearchOpen(true)} aria-label="Cambiar cliente">
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {onClienteClear ? (
                      <Tooltip title="Quitar cliente">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={onClienteClear}
                          aria-label="Quitar cliente"
                        >
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                  </>
                ) : null}
              </Stack>
            </Stack>
            <ClienteFacturacionAvisos
              tipoCliente={cliente.tipoCliente}
              notas={cliente.notas}
              bloqueoModo={bloqueoClienteSuspendido}
            />
          </CardContent>
        </Card>
      ) : null}
    </Box>
  );
};

export default FacturaPOSClientePanel;
